import { action, batch, reaction } from '@reatom/framework'

import { Duration } from '@blade/std-lib'
import { initializeBasicPollingReaction } from '@root/entities/basic'
import { initializeNetworkDataPoolingReaction } from '@root/entities/network'
import { prefetchUrlPairAction } from '@root/entities/pair/atoms'
import { initializePlatformAction } from '@root/entities/platform'
import { openPremiumCurrencyDetailsEvent } from '@root/entities/premium'
import { initializeWalletAtoms } from '@root/entities/wallet'
import { initializeBrowserLocalWarningDialogAction } from '@root/features/announcements/browser-local-warning-dialog/atoms'
import { initializeWebSignInAction } from '@root/features/auth/web-sign-in'
import { initializeBlacklistCurrencies } from '@root/features/blacklist/blacklist-currencies/atoms'
import { initializeFavoritePairsAction } from '@root/features/favorite-pairs'
import {
  onInitializationReadyAction,
  onInitializationReadyOrWebsiteAction,
} from '@root/features/initialization/atoms/common'
import { initializeInitializationAction } from '@root/features/initialization/atoms/desktop'
import { initializeHeliusRpcLinksAction } from '@root/features/network/atoms'
import { initializeOrdersSocketAction } from '@root/features/orders/atoms'
import { initializeClipboardPairAction } from '@root/features/pair/atoms/reatom-clipboard-pair'
import { initializePairActivitySocketAction } from '@root/features/pair-activity/atoms'
import { initializePremiumFeedTableAction } from '@root/features/pair-activity/atoms/reatom-premium-feed-table'
import { initializeNativePricesAction } from '@root/features/realtime/atoms'
import { joinReferralCodeReaction } from '@root/features/referral/atoms'
import { initializeApplicationRouting } from '@root/features/routing/atoms'
import { initializeBlockhashAction } from '@root/features/services/blockhash/atoms'
import { initializeFeeConfigAction } from '@root/features/services/fee-config/atoms'
import {
  dependenciesAtom,
  initializeDependencies,
} from '@root/features/shared/dependencies'
import { initializeWorkerAction } from '@root/features/shared-worker/atoms'
import { listenAppShortcuts } from '@root/features/shortcut/app-shortcuts'
import { initializeSupportTickets } from '@root/features/support/atoms/reatom-support-ticket'
import { initializeSupportTicketsAdmin } from '@root/features/support/atoms/reatom-support-ticket-admin'
import { initializeSupportWsTokenAction } from '@root/features/support/atoms/reatom-support-ws-token'
import { initializePositionSocketDataAction } from '@root/features/trade/position/atoms'
import { initializeTradeSettingsAction } from '@root/features/trade/trade-settings'
import {
  initializeTradingAction,
  initializeTradingWalletAction,
} from '@root/features/trade/trading'
import { initializeWalletTrackerAction } from '@root/features/wallet-tracker/atoms'
import { initializeTradePageAction } from '@root/pages/trade/trade-page/atoms'
import {
  AppInvisibleState,
  appVisibilityAtom,
  initializeScaleFactorAction,
  initializeVisibilityAction,
} from '@root/shared/core'
import { listenActivityState } from '@root/shared/lib'
import { premiumCurrencyDialogAtom } from '@root/widgets/premium/premium-currency-details/atom/premium-currency-dialog'

import {
  initializeMessaging,
  initializeOptionsDialogOpen,
  initializePremiumFlowsAction,
  initializeReferralCodeProfileUpdate,
} from './initialize-common'
import { onboardingDialogsModel } from './reatom-onboarding-dialogs'

export const initializeDesktopAppAction = action(async (ctx) => {
  initializeDependencies(ctx)

  /**
   * Before anything else, because nothing else is waiting on an answer.
   *
   * On a pair page the address is in the url before the document is, yet
   * `pair-info` used to leave at 2,368ms - after the session, the sockets and
   * thirty initialisers - and every block on the page is behind it. Asked for
   * here it leaves with the first batch of requests instead, and the page
   * finds the pair already in the registry.
   */
  prefetchUrlPairAction(ctx)

  const { mainWebsocket, pathfinderWsClient } = ctx.get(dependenciesAtom)
  mainWebsocket.initializeSocketAction(ctx)
  pathfinderWsClient.openSocket()

  initializeApplicationRouting(ctx)
  initializeWorkerAction(ctx)
  initializePlatformAction(ctx)
  initializeHeliusRpcLinksAction(ctx)
  initializeScaleFactorAction(ctx)
  initializeVisibilityAction(ctx)
  initializeSupportTickets(ctx)
  initializeSupportTicketsAdmin(ctx)
  initializeTradingAction(ctx)
  initializeBlockhashAction(ctx)
  initializeNetworkDataPoolingReaction(ctx)
  initializeBasicPollingReaction(ctx)
  initializeWebSignInAction(ctx)
  initializeNativePricesAction(ctx)
  listenActivityState(ctx)
  initializeTradeSettingsAction(ctx)
  initializePremiumFeedTableAction(ctx)
  initializeWalletTrackerAction(ctx)
  initializeFavoritePairsAction(ctx)
  initializePremiumFlowsAction(ctx)
  initializePairActivitySocketAction(ctx)
  initializeFeeConfigAction(ctx)
  initializeSupportWsTokenAction(ctx)
  initializeBrowserLocalWarningDialogAction(ctx)
  initializeBlacklistCurrencies(ctx)
  onboardingDialogsModel.initialize(ctx)
  initializeClipboardPairAction(ctx)

  onInitializationReadyOrWebsiteAction(ctx, () => {
    const { chartWebsocket } = ctx.get(dependenciesAtom)
    batch(ctx, () => {
      chartWebsocket.initializeAction(ctx)
    })
  })

  listenAppShortcuts(ctx)
  onInitializationReadyAction(ctx, () => {
    batch(ctx, () => {
      initializeWalletAtoms(ctx)
      initializeTradingWalletAction(ctx)
      initializePositionSocketDataAction(ctx)
      initializeOrdersSocketAction(ctx)
    })
  })

  await ctx.schedule(() => initializeInitializationAction(ctx))

  openPremiumCurrencyDetailsEvent.onCall((ctx, _, [{ currencyAddress }]) => {
    premiumCurrencyDialogAtom(ctx, currencyAddress)
  })

  initializeReferralCodeProfileUpdate()
  initializeMessaging(ctx)
  joinReferralCodeReaction(ctx)
  initializeOptionsDialogOpen()
  initializeTradePageAction(ctx)
  inactiveAppReaction(ctx)
}, 'initializeDesktopAppAction')

const INACTIVE_RELOAD_DURATION = Duration.hours(2)
const inactiveAppReaction = reaction((ctx) => {
  const appVisibility = ctx.spy(appVisibilityAtom)
  if (appVisibility instanceof AppInvisibleState) {
    return
  }
  if (
    appVisibility.inactiveDuration &&
    Duration.isGreaterThan(
      appVisibility.inactiveDuration,
      INACTIVE_RELOAD_DURATION,
    )
  ) {
    location.reload()
  }
}, 'inactiveAppReaction')
